// src/sections/Games.tsx

import React, { useState } from 'react';
import clsx from 'clsx';
import { Gamepad2, Joystick, Bot } from 'lucide-react';

import PortfolioItem from '../components/PortfolioItem';

// Fewer, all-lowercase filters
type GamesFilter = 'all' | '2d' | '3d' | 'ai';

export interface GameData {
  title: string;
  description: string;
  category: GamesFilter;
  icon: React.ReactNode;
  link: string;
  size: 'small' | 'large';
  image?: string;
  video?: string;
}

export const gamesData: GameData[] = [
    {
      title: 'Social Puzzle',
      description: 'A multiplayer 3D jigsaw puzzle game with images/video/livestream support.',
      category: '3d',
      icon: <Gamepad2 className="w-4 h-4" />,
      video: 'assets/games/socialpuzzle/socialpuzzle1.mp4',
      link: 'games/social-puzzle',
      size: 'large',
    },
    {
      title: 'Time Trodden',
      description: 'A 2D pixel-art platformer with quirky mechanics.',
      category: '2d',
      icon: <Joystick className="w-4 h-4" />,
      video: 'assets/games/timetrodden/timetrodden1.mp4',
      link: 'games/time-trodden',
      size: 'small',
    },
    {
      title: 'Princess & Protector',
      description: 'An endless wave-based game with upgrades.',
      category: 'ai',
      icon: <Bot className="w-4 h-4" />,
      video: 'assets/games/princessprotector/princessprotector.mp4',
      link: 'games/princess-and-protector',
      size: 'small',
    },
    {
      title: 'Good Knight',
      description: 'A short RPG about trying to do good.',
      category: '2d',
      icon: <Joystick className="w-4 h-4" />,
      image: 'assets/games/goodknight/good.png',
      video: 'assets/games/goodknight/good.mp4',
      link: 'games/good-knight',
      size: 'small',
    },
    {
      title: 'Hallowed Island',
      description: '3D Halloween-themed dungeon crawl FPS.',
      category: '3d',
      icon: <Gamepad2 className="w-4 h-4" />,
      video: 'assets/games/hallowedisland/hhtrailer.mp4',
      link: 'games/hallowed-island',
      size: 'small',
    },
    {
      title: 'Another One',
      description: 'Demo showcasing procedural animations.',
      category: '3d',
      icon: <Gamepad2 className="w-4 h-4" />,
      video: 'assets/games/anotherone/one.mp4',
      link: 'games/another-one',
      size: 'small',
    },
];

const Games: React.FC = () => {
  const [filter, setFilter] = useState<GamesFilter>('all');

  // Filter logic
  const filteredGames = gamesData.filter((item) => {
    if (filter === 'all') return true;
    return item.category === filter;
  });

  // Filter buttons, all-lowercase
  const filterButtons = [
    { label: 'all', value: 'all' },
    { label: '2d', value: '2d', icon: <Joystick className="w-4 h-4" /> },
    { label: '3d', value: '3d', icon: <Gamepad2 className="w-4 h-4" /> },
    { label: 'ai', value: 'ai', icon: <Bot className="w-4 h-4" /> },
  ];

  return (
    <section id="games" className="section">
      <div className="max-w-7xl mx-auto px-4 py-20">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-8">
          Games
        </h1>

        {/* Filter Buttons */}
        <div className="flex justify-center mb-8 space-x-4 flex-wrap">
          {filterButtons.map((btn) => (
            <button
              key={btn.value}
              onClick={() => setFilter(btn.value as GamesFilter)}
              className={clsx(
                'filter-btn px-4 py-2 rounded-full flex items-center space-x-2 transition-colors mb-2',
                {
                  'bg-blue-600 text-white hover:bg-blue-700': filter === btn.value,
                  'bg-gray-200 dark:bg-gray-700 hover:bg-blue-600 hover:text-white':
                    filter !== btn.value,
                }
              )}
            >
              {btn.icon}
              <span>{btn.label}</span>
            </button>
          ))}
        </div>

        {/* Games Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
          {filteredGames.map((item) => (
            <PortfolioItem
              key={item.title} // Stable key
              data={item}
              className={clsx(
                'col-span-1',
                item.size === 'large' && 'sm:col-span-2'
              )}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Games;
